import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import { useCart } from '@/contexts/CartContext';
import { useProducts } from '@/contexts/ProductsContext';
import {
  checkGatewayPaymentStatus,
  saveReceiptCopy,
  loadPendingCheckoutSnapshot,
  clearPendingCheckoutSnapshot,
  type GatewayPaymentStatus,
} from '@/lib/coreOrder';
import { trackPurchase } from '@/lib/tracking';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';

function parsePrice(p: string) {
  return Number(p.replace(/[^0-9]/g, ''));
}

// SSLCommerz(등)에서 돌아온 뒤 결제가 실제로 확정됐는지 폴링으로 확인하는
// 페이지. core-sslcommerz-callback이 브라우저를 여기로 돌려보낼 때는 결과를
// URL에 싣지 않는다(조작 방지) — 그래서 이 페이지 스스로
// core-check-payment-status를 반복 호출해 서버가 확정한 상태를 기다린다.
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // 최대 약 60초

type ViewState = 'polling' | 'success' | 'failed' | 'cancelled' | 'timeout';

export default function PaymentResultPage() {
  const { tranId } = useParams<{ tranId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const { clearCart } = useCart();
  const { products, routineSet } = useProducts();

  const getProductInfo = (slug: string) => {
    if (slug === '3-step-set') {
      return routineSet ? { name: routineSet.name, price: routineSet.price } : null;
    }
    const p = products.find((x) => x.id === slug);
    return p ? { name: p.name, price: p.price } : null;
  };

  const [view, setView] = useState<ViewState>('polling');
  const attemptsRef = useRef(0);
  const doneRef = useRef(false);

  useEffect(() => {
    if (!tranId) {
      setView('failed');
      return;
    }

    let cancelled = false;

    const finish = (result: GatewayPaymentStatus) => {
      if (doneRef.current) return;
      doneRef.current = true;

      if (result.status === 'completed') {
        const snapshot = loadPendingCheckoutSnapshot(tranId);
        const orderRef = result.orderNo ?? result.orderId ?? tranId;
        const subtotal = result.subtotalBdt ?? 0;
        const shippingCost = result.shippingBdt ?? 0;
        const discount = result.discountBdt ?? 0;
        saveReceiptCopy({
          id: orderRef,
          orderNo: result.orderNo,
          orderId: result.orderId,
          items: snapshot?.items ?? [],
          contact: snapshot?.contact ?? { fullName: '', phone: '' },
          shipping: snapshot?.shipping ?? { address1: '', city: 'Dhaka' },
          payment: { method: snapshot?.paymentMethod ?? 'gateway' },
          subtotal,
          discount,
          shippingCost,
          total: subtotal - discount + shippingCost,
          createdAt: new Date().toISOString(),
        });

        // Previously missing entirely on this path — an online-gateway
        // order never fires Purchase without this, since the browser left
        // the SPA for the gateway and came back on a fresh page load, well
        // past wherever a page-load-only pixel snippet would have fired.
        trackPurchase(
          orderRef,
          (snapshot?.items ?? []).map((item) => {
            const info = getProductInfo(item.slug);
            return { id: item.slug, name: info?.name ?? item.slug, price: info ? parsePrice(info.price) : 0, quantity: item.qty };
          }),
          subtotal - discount + shippingCost,
        );

        clearPendingCheckoutSnapshot(tranId);
        clearCart();
        navigate(`${prefix}/order/${encodeURIComponent(orderRef)}`, { replace: true });
        return;
      }

      setView(result.status === 'cancelled' ? 'cancelled' : 'failed');
    };

    const poll = async () => {
      attemptsRef.current += 1;
      const result = await checkGatewayPaymentStatus(tranId);
      if (cancelled) return;

      if (result && result.status !== 'initiated') {
        finish(result);
        return;
      }

      if (attemptsRef.current >= MAX_POLLS) {
        setView('timeout');
        return;
      }

      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tranId]);

  const goToCheckout = () => navigate(`${prefix}/checkout`);
  const goHome = () => navigate(prefix || '/');

  return (
    <>
      <SharedNavbar />
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-16">
        <div className="max-w-xl mx-auto px-6 md:px-12 py-10">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-[#E8E8E6] flex flex-col items-center text-center gap-6">
            {view === 'polling' && (
              <>
                <div className="w-10 h-10 border-2 border-[#E8E8E6] border-t-[#7B1113] rounded-full animate-spin" />
                <div>
                  <h1 className="font-serif text-xl md:text-2xl font-bold text-[#2A2A2A] mb-2">
                    {t('payment.confirming', 'Confirming your payment…')}
                  </h1>
                  <p className="text-sm text-[#6B6B6B]">
                    {t('payment.confirmingSub', "Please don't close this page.")}
                  </p>
                </div>
              </>
            )}

            {(view === 'failed' || view === 'cancelled') && (
              <>
                <h1 className="font-serif text-xl md:text-2xl font-bold text-[#2A2A2A]">
                  {view === 'cancelled'
                    ? t('payment.cancelled', 'Payment cancelled')
                    : t('payment.failed', 'Payment failed')}
                </h1>
                <p className="text-sm text-[#6B6B6B]">
                  {t('payment.failedSub', 'Your cart is still saved — you can try again or choose Cash on Delivery.')}
                </p>
                <button
                  onClick={goToCheckout}
                  className="w-full sm:w-auto px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
                >
                  {t('payment.tryAgain', 'Back to Checkout')}
                </button>
              </>
            )}

            {view === 'timeout' && (
              <>
                <h1 className="font-serif text-xl md:text-2xl font-bold text-[#2A2A2A]">
                  {t('payment.timeout', "Still confirming — this is taking longer than usual")}
                </h1>
                <p className="text-sm text-[#6B6B6B]">
                  {t(
                    'payment.timeoutSub',
                    "If the payment succeeded, you'll receive a confirmation shortly. Otherwise please contact us before trying again.",
                  )}
                </p>
                <button
                  onClick={goHome}
                  className="w-full sm:w-auto px-6 py-2.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors cursor-pointer text-center"
                >
                  {t('common.continueShopping')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <SharedFooter />
    </>
  );
}
